import {
  createEffect,
  createMemo,
  createResource,
  For,
  onCleanup,
  onMount,
  Show,
} from "solid-js";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import type { PowerNow, SiteNode } from "@energyplatform/client";
import { api } from "../services.ts";
import { locale, t } from "../i18n.ts";
import { formatNumber } from "../format.ts";
import { scope, setScope, siteTree } from "../context.ts";
import { subtreeOf } from "../tree.ts";
import { freshness } from "../overview.ts";
import { alarmedAt, geoOf, type Geo } from "../plans.ts";

/** Power now is fetched for at most this many places; the rest are shown without it. */
const MAX_WITH_POWER = 12;

interface Place {
  node: SiteNode;
  geo: Geo;
  now: PowerNow | null;
  /** Points below the place without current data. */
  stale: number;
}

// Leaflet sets SVG attributes, where CSS variables do not apply: the theme's colours are read once.
const color = (name: string) =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim() ||
  "#888";

/**
 * The scope place and its buildings and sources on a map (prototype "Mapa zakładu"): the places below the scope that
 * have their own position, and the scope itself, which inherits its parent's when it has none. Coloured by whether a
 * point needs attention, sized by power now.
 */
export function PlaceMap(props: { nodeId: string }) {
  const placed = createMemo(() => {
    const nodes = siteTree()?.nodes ?? [];
    const inside = subtreeOf(nodes, props.nodeId);
    return inside
      .map((id) => nodes.find((n) => n.id === id)!)
      .filter(
        (n) =>
          n.id === props.nodeId ||
          (n.latitude !== null && n.longitude !== null),
      )
      .flatMap((n) => {
        const geo = geoOf(nodes, n.id);
        return geo ? [{ node: n, geo }] : [];
      });
  });
  const [events] = createResource(() => api.events.list());
  const [places] = createResource(
    () => (placed().length > 0 ? placed() : undefined),
    async (all) => {
      return Promise.all(
        all.map(async ({ node, geo }, i): Promise<Place> => {
          const now =
            i < MAX_WITH_POWER
              ? await api.sites.now(node.id).catch(() => null)
              : null;
          const stale = (now?.points ?? []).filter(
            (p) => freshness(p.at, Date.now()) !== "ok",
          ).length;
          return { node, geo, now, stale };
        }),
      );
    },
  );
  /** Points below the place that have an open event or no current data. */
  const attention = (p: Place) => {
    const tree = siteTree();
    const inside = new Set(subtreeOf(tree?.nodes ?? [], p.node.id));
    const alarmed = alarmedAt(events() ?? [], Date.now());
    return (
      p.stale +
      (tree?.points ?? []).filter(
        (x) => inside.has(x.nodeId) && alarmed.has(x.id),
      ).length
    );
  };
  const inherited = (p: Place) =>
    p.geo.from === p.node.id
      ? null
      : (siteTree()?.nodes.find((n) => n.id === p.geo.from)?.name ?? "");
  const power = (p: Place) =>
    p.now?.hasBoundary
      ? `${formatNumber(p.now.consumption, locale(), 1)} kW`
      : "—";

  let host!: HTMLDivElement;
  let map: L.Map | undefined;
  let layer: L.LayerGroup | undefined;
  onMount(() => {
    map = L.map(host, { scrollWheelZoom: false, attributionControl: true });
    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      className: "street-tiles",
      maxZoom: 19,
      attribution: "© OpenStreetMap",
    }).addTo(map);
    layer = L.layerGroup().addTo(map);
    onCleanup(() => map?.remove());
  });

  // The popup is built from DOM nodes: names are user text and must not be parsed as HTML.
  const popup = (p: Place) => {
    const box = document.createElement("div");
    const name = document.createElement("b");
    name.textContent = p.node.name;
    const line = document.createElement("div");
    line.textContent = `${t("plans.site.now")}: ${power(p)} · ${
      attention(p) > 0
        ? t("plans.site.attention", { count: attention(p) })
        : t("plans.site.ok")
    }`;
    box.append(name, line);
    if (scope()?.id !== p.node.id) {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "btn sm";
      button.style.marginTop = "6px";
      button.textContent = t("plans.site.setScope");
      button.onclick = () => setScope(p.node.id);
      box.append(button);
    }
    return box;
  };

  createEffect(() => {
    const all = places();
    if (!map || !layer || !all || all.length === 0) {
      return;
    }
    layer.clearLayers();
    const bounds: L.LatLngTuple[] = [];
    for (const p of all) {
      const at: L.LatLngTuple = [p.geo.latitude, p.geo.longitude];
      bounds.push(at);
      const kw = Math.abs(p.now?.consumption ?? 0);
      L.circleMarker(at, {
        radius: Math.max(7, Math.min(16, Math.sqrt(kw || 4) * 1.6)),
        color: "#fff",
        weight: 2,
        fillColor: color(attention(p) > 0 ? "--warn" : "--ok"),
        fillOpacity: 0.9,
      })
        .bindTooltip(p.node.name)
        .bindPopup(() => popup(p))
        .addTo(layer);
    }
    if (bounds.length === 1) {
      map.setView(bounds[0]!, 18);
    } else {
      // As close as the map goes while every place stays in view.
      map.fitBounds(L.latLngBounds(bounds), { padding: [30, 30], maxZoom: 19 });
    }
  });

  return (
    <Show
      when={placed().length > 0}
      fallback={
        <section class="card empty">
          <p class="muted">{t("plans.site.none")}</p>
        </section>
      }
    >
      <section class="card">
        <div class="mapgrid">
          <div style={{ padding: "10px" }}>
            <div
              ref={host}
              class="map"
              style={{ height: "440px" }}
              role="region"
              aria-label={t("plans.tabs.site")}
            />
          </div>
          <div class="side">
            <table class="tbl">
              <thead>
                <tr>
                  <th>{t("plans.site.place")}</th>
                  <th class="r">{t("plans.site.now")}</th>
                </tr>
              </thead>
              <tbody>
                <For each={places() ?? []}>
                  {(p) => (
                    <tr
                      class={`click${scope()?.id === p.node.id ? " sel" : ""}`}
                      onClick={() => {
                        map?.setView([p.geo.latitude, p.geo.longitude], 17);
                      }}
                    >
                      <td>
                        <b style={{ "font-weight": 500 }}>{p.node.name}</b>
                        <div class="hint">
                          {inherited(p) === null
                            ? t("plans.site.own")
                            : t("plans.site.inherited", {
                                from: inherited(p)!,
                              })}
                        </div>
                        <Show when={attention(p) > 0}>
                          <span class="chip warn">
                            {t("plans.site.attention", { count: attention(p) })}
                          </span>
                        </Show>
                      </td>
                      <td class="r num">{power(p)}</td>
                    </tr>
                  )}
                </For>
              </tbody>
            </table>
          </div>
        </div>
        <div class="legend">
          <span>
            <i class="sw box" style={{ background: "var(--ok)" }} />
            {t("plans.site.legendOk")}
          </span>
          <span>
            <i class="sw box" style={{ background: "var(--warn)" }} />
            {t("plans.site.legendAttention")}
          </span>
          <span>{t("plans.site.legendSize")}</span>
        </div>
      </section>
    </Show>
  );
}
